import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Users, Shield, Zap, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import GironeBadge from "@/components/GironeBadge";
import { inferPlayerEvents } from "@/lib/inferPlayerEvents";

const ROLE_META = {
  POR: { label: "P",  color: "#f59e0b", full: "Portieri" },
  DIF: { label: "D",  color: "#22c55e", full: "Difensori" },
  CEN: { label: "C",  color: "#3b82f6", full: "Centrocampisti" },
  ATT: { label: "A",  color: "#ef4444", full: "Attaccanti" },
  P:   { label: "P",  color: "#f59e0b", full: "Portieri" },
  D:   { label: "D",  color: "#22c55e", full: "Difensori" },
  C:   { label: "C",  color: "#3b82f6", full: "Centrocampisti" },
  A:   { label: "A",  color: "#ef4444", full: "Attaccanti" },
};

export default function MvpBreakdownModal({ matchday, teamId, teamName, onClose, challengeType = "campionato" }) {
  const open = Boolean(matchday && teamId);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!open) { setData(null); setErr(false); return; }
    setLoading(true); setErr(false);
    api.get(`/public/matchday/${matchday}/team/${teamId}/breakdown`)
      .then(({ data }) => setData(data))
      .catch(() => setErr(true))
      .finally(() => setLoading(false));
  }, [open, matchday, teamId]);

  // Group roster by role
  const groups = { POR: [], DIF: [], CEN: [], ATT: [] };
  (data?.roster || []).forEach((p) => {
    const r = (p.role || "").toUpperCase();
    if (groups[r]) groups[r].push(p);
    else if (r === "P") groups.POR.push(p);
    else if (r === "D") groups.DIF.push(p);
    else if (r === "C") groups.CEN.push(p);
    else if (r === "A") groups.ATT.push(p);
  });

  const modifiersList = data?.match?.modifiers
    ? Object.entries(data.match.modifiers).filter(([, v]) => Number(v) !== 0)
    : [];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="mvp-breakdown-modal">
        <DialogHeader>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Dettaglio giornata {matchday}</div>
          <DialogTitle className="display font-black text-2xl md:text-3xl uppercase tracking-tight break-words">
            {teamName || data?.team_name || "Squadra"}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-16 text-slate-500">
            <Loader2 className="w-6 h-6 mr-3 animate-spin" /> Caricamento dettaglio…
          </div>
        )}
        {err && <div className="text-slate-500 py-8 text-center">Impossibile caricare il dettaglio.</div>}

        {data && !loading && (
          <div className="space-y-5">
            {/* Header stats — Punti totali · Girone · Modulo */}
            <div className="grid grid-cols-3 gap-3">
              <StatBox label="Punti totali" value={data.points?.toFixed(1)} color="#eab308" />
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Girone</div>
                <div className="mt-1 flex items-center gap-1.5">
                  {data.girone && <GironeBadge girone={data.girone} size={18} />}
                  <span className="display font-black text-lg">{data.girone || "—"}</span>
                </div>
              </div>
              <StatBox label="Modulo" value={data.match?.module || "—"} color="#3b56e0" />
            </div>

            {/* H2H compatto formato "3 (83 pt.) a 3 (82 pt.)" per Campionato.
                Per Tutto o Niente (challengeType='ton') si userà un layout separato. */}
            {data.match && (
              <div className="card-surface rounded-2xl p-4" data-testid="mvp-h2h">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2">
                  <TrendingUp className="w-3.5 h-3.5" /> Testa a testa · Giornata {matchday}
                </div>
                {challengeType === "ton" ? (
                  // Layout Tutto o Niente: due colonne opposte con nomi + punteggi
                  <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-center">
                    <div className="text-right">
                      <div className="display font-black text-base md:text-lg uppercase truncate">{data.team_name}</div>
                      <div className="text-slate-500 text-xs">{data.match.own_totale?.toFixed(1)} pt</div>
                    </div>
                    <div className="display font-black text-2xl md:text-3xl text-slate-900 tabular-nums">
                      {data.match.own_score ?? "-"} <span className="text-slate-400">·</span> {data.match.opponent_score ?? "-"}
                    </div>
                    <div className="text-left">
                      <div className="display font-black text-base md:text-lg uppercase truncate">{data.match.opponent_team || "—"}</div>
                      <div className="text-slate-500 text-xs">{data.match.opponent_totale?.toFixed(1)} pt</div>
                    </div>
                  </div>
                ) : (
                  // Formato compatto Campionato: "TEAM A vs TEAM B · 3 (83 pt.) a 3 (82 pt.)"
                  <div className="space-y-2">
                    <div className="text-sm text-slate-600 text-center">
                      <span className="display font-black uppercase text-slate-900">{data.team_name}</span>
                      <span className="mx-2 text-slate-400">vs</span>
                      <span className="display font-black uppercase text-slate-900">{data.match.opponent_team || "—"}</span>
                    </div>
                    <div className="display font-black text-2xl md:text-3xl text-slate-900 tabular-nums text-center">
                      <span>{data.match.own_score ?? "-"}</span>
                      <span className="text-sm text-slate-500 font-semibold ml-1.5">
                        ({data.match.own_totale != null ? Number(data.match.own_totale).toFixed(1) : "-"} pt.)
                      </span>
                      <span className="mx-3 text-slate-400 text-xl">a</span>
                      <span>{data.match.opponent_score ?? "-"}</span>
                      <span className="text-sm text-slate-500 font-semibold ml-1.5">
                        ({data.match.opponent_totale != null ? Number(data.match.opponent_totale).toFixed(1) : "-"} pt.)
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Modifiers */}
            {modifiersList.length > 0 && (
              <div className="card-surface rounded-2xl p-4">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2">
                  <Zap className="w-3.5 h-3.5" /> Modificatori applicati
                </div>
                <div className="flex flex-wrap gap-2">
                  {modifiersList.map(([k, v]) => (
                    <div key={k} className="inline-flex items-center gap-2 rounded-full bg-slate-100 border border-slate-200 px-3 py-1.5 text-xs">
                      <span className="capitalize font-semibold text-slate-700">{k}</span>
                      <span className="font-black tabular-nums" style={{ color: Number(v) >= 0 ? "#22c55e" : "#ef4444" }}>
                        {Number(v) >= 0 ? "+" : ""}{Number(v).toFixed(1)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Players from giornata_results — RAGGRUPPATI PER RUOLO con punti reali */}
            {data.match?.players?.length > 0 ? (
              <div className="card-surface rounded-2xl p-4" data-testid="mvp-players-live">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-2">
                  <Shield className="w-3.5 h-3.5" /> Punteggi calciatori · Giornata {matchday}
                </div>
                {["POR", "DIF", "CEN", "ATT"].map((role) => {
                  const arr = (data.match.players || []).filter((p) => {
                    const r = (p.role || "").toUpperCase();
                    return r === role || (role === "POR" && r === "P") || (role === "DIF" && r === "D") || (role === "CEN" && r === "C") || (role === "ATT" && r === "A");
                  }).slice().sort((a, b) => {
                    // Titolari sempre sopra le riserve
                    const aStarter = a.starter !== false ? 1 : 0;
                    const bStarter = b.starter !== false ? 1 : 0;
                    if (aStarter !== bStarter) return bStarter - aStarter;
                    // A parità, fantavoto DESC
                    const av = Number(a.fantavoto ?? a.voto_base ?? -Infinity);
                    const bv = Number(b.fantavoto ?? b.voto_base ?? -Infinity);
                    return bv - av;
                  });
                  if (!arr.length) return null;
                  const meta = ROLE_META[role];
                  return (
                    <div key={role} className="mb-3 last:mb-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded text-white text-[10px] font-black" style={{ background: meta.color }}>
                          {meta.label}
                        </span>
                        <span className="text-xs uppercase tracking-widest font-black text-slate-700">{meta.full}</span>
                        <span className="text-[10px] text-slate-400">({arr.length})</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                        {arr.map((p, i) => {
                          const pts = p.fantavoto ?? p.voto_base;
                          const isStarter = p.starter !== false;
                          const events = inferPlayerEvents(p.role, p.voto_base, p.fantavoto);
                          return (
                            <div key={i} className={`flex items-center gap-2 py-1.5 px-2 rounded ${isStarter ? "" : "opacity-60"}`}
                                 data-testid={`mvp-player-${i}`}>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-semibold text-slate-900 truncate">{p.name || "—"}</div>
                                <div className="flex items-center gap-1 flex-wrap mt-0.5">
                                  {!isStarter && <span className="text-[10px] text-slate-400 mr-1">Panchina</span>}
                                  {events.map((ev, k) => (
                                    <span key={`${ev.key}-${k}`}
                                          title={ev.label}
                                          data-testid={`event-${ev.key}`}
                                          className="inline-flex items-center justify-center w-[18px] h-[18px] rounded-full text-[10px] leading-none"
                                          style={{ background: ev.bg, color: "#fff" }}>
                                      {ev.emoji}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <span className="tabular-nums font-black text-sm shrink-0"
                                    style={{ color: (pts ?? 0) >= 6 ? "#22c55e" : "#ef4444" }}>
                                {pts != null ? Number(pts).toFixed(1) : "-"}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : data.match && (
              <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                Dettaglio dei singoli voti calciatori non disponibile per questa giornata. Carica l'Excel della giornata dal pannello Admin per vedere i punteggi di ogni calciatore.
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function StatBox({ label, value, color }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="display font-black text-2xl tabular-nums" style={{ color }}>{value}</div>
    </div>
  );
}
